import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Navigation, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const trimmedUsername = username.trim().toLowerCase();
    const trimmedPassword = password.trim();
    
    console.log('Attempting login with:', { username: trimmedUsername, passwordLength: trimmedPassword.length });

    try {
      const user = await login(trimmedUsername, trimmedPassword);
      console.log('Login successful:', user);
      toast.success(`Welcome, ${user.username}!`);
    } catch (err) {
      console.error('Login error:', err);
      console.error('Error response:', err.response?.data);
      const message = err.response?.data?.detail || err.message || 'Login failed';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f5f7] flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#007aff] rounded-2xl mb-4 shadow-lg">
            <Navigation className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-[#1d1d1f]">Route-Z</h1>
          <p className="text-[#86868b] mt-2 text-base">Postcode Route Mapper</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-5 bg-white p-8 rounded-2xl shadow-[0_2px_20px_rgba(0,0,0,0.08)]">
          <div className="space-y-2">
            <Label htmlFor="username" className="text-sm font-medium text-[#1d1d1f]">Username</Label>
            <Input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter username"
              className="h-12 text-base rounded-xl bg-[#f5f5f7] border-0 focus:ring-2 focus:ring-[#007aff] placeholder:text-[#86868b]"
              data-testid="username-input"
              autoComplete="username"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium text-[#1d1d1f]">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="h-12 text-base rounded-xl bg-[#f5f5f7] border-0 focus:ring-2 focus:ring-[#007aff] placeholder:text-[#86868b]"
              data-testid="password-input"
              autoComplete="current-password"
              required
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-[#ff3b30]/10 rounded-xl text-[#ff3b30] text-sm" data-testid="login-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 text-base font-semibold bg-[#007aff] hover:bg-[#0066d6] rounded-xl transition-all duration-200"
            data-testid="login-submit"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </Button>
        </form>

        {/* Footer hint */}
        <p className="text-center text-sm text-[#86868b] mt-6">
          Contact your administrator for login credentials
        </p>
      </div>
    </div>
  );
}
